import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { arrayOf, string, number, exact, func, shape } from 'prop-types';
import { useSelector } from 'react-redux';

import { Icon, Loader, Timeline } from '@atoms';
import { Flex, Typography, Button } from '@mixins';
import { ROOM_TYPES_BY_NAME } from '@constants/common';
import { getCloudinaryImageLinkBySize, getCurrency, getFloorLabel } from '@helpers/common';
import { getTimelineClickRange, isTimelineChangeValid } from '@helpers/time';
import { theme } from '@styles';

import VideoConferencing from '@assets/svgs/VideoConferencing.svg';
import { ReactComponent as Credits } from '@assets/svgs/Credits.svg';
import { ReactComponent as Seats } from '@assets/svgs/Seats.svg';
import RoomImage from '@assets/images/RoomImage.png';
import PricingDetails from '../PricingDetails';

const IMAGE_WIDTH = 269;

const RoomCard = ({
  roomType,
  initialStartTime,
  initialEndTime,
  creditPrices,
  maxBookingHours,
  minBookingHours,
  ruleMinBookingHours,
  defaultBookingHours,
  id,
  imageUrl,
  number: roomNumber,
  floor,
  seats,
  creditsPrice,
  currencyPrice,
  availableTimes,
  features,
  onRoomClick,
  timelineScrollTo,
  bookings,
  timeList,
}) => {
  const { user } = useSelector(store => store.userStore);
  const { currentLocation } = useSelector(store => store.locationStore);

  const [isLoading, setIsLoading] = useState(true);
  const [startTime, setStartTime] = useState(-1);
  const [endTime, setEndTime] = useState(-1);

  useEffect(() => {
    if ((initialEndTime || initialEndTime === 0) && (initialStartTime || initialStartTime === 0)) {
      setStartTime(initialStartTime);
      setEndTime(initialEndTime);
    }
    setTimeout(() => setIsLoading(false), 500);
  }, [initialEndTime, initialStartTime, timeList]);

  const isMinBookingDetailsShown = roomType === ROOM_TYPES_BY_NAME.Office && !currentLocation.isSaltoAvailable;

  const roomImage = useMemo(() => getCloudinaryImageLinkBySize(imageUrl, IMAGE_WIDTH), [imageUrl]);

  const isSelectedTimelineAvailable = useMemo(
    () => timeList.slice(startTime, endTime).every(timeline => availableTimes.includes(timeline)),
    [availableTimes, endTime, startTime, timeList],
  );

  const handleTimeClick = time => () => {
    if (time !== startTime) {
      const range = getTimelineClickRange({
        time,
        hours: defaultBookingHours,
        timeList,
        availableTimes,
        allowSingleSlot: !user.isActivate && minBookingHours < 1,
      });

      if (range) {
        setStartTime(range[0]);
        setEndTime(range[1]);
      }
    }
  };

  const handleTimeChange = useCallback(
    range => {
      if (isTimelineChangeValid({ range, minBookingHours, maxBookingHours, timeList, availableTimes })) {
        setStartTime(range[0]);
        setEndTime(range[1]);
      }
    },
    [availableTimes, maxBookingHours, minBookingHours, timeList],
  );

  const featuresBlock = useMemo(
    () =>
      features.map(({ id: featureId, iconUrl, displayName }) => (
        <Flex mb={10} mr={['6px', '8px', 10]} alignItems="center" key={featureId}>
          <svg width="20" height="20">
            <image xlinkHref={iconUrl || VideoConferencing} width="20" height="20" />
          </svg>
          <Typography
            ml={['4px', '6px', '8px']}
            variant="diatype-400"
            fontSize={[10, 10, 12, 14]}
            color={theme.color.gray[300]}>
            {displayName}
          </Typography>
        </Flex>
      )),
    [features],
  );

  return (
    <Flex
      border="1px solid #EFECE6"
      boxShadow="0px 8px 30px rgba(80, 85, 136, 0.06)"
      borderRadius="2px"
      mb={[10, 10, 15, 20]}
      p={[10, 10, 15, 20]}
      width="100%">
      <Flex
        background={`url(${roomImage || RoomImage}) center no-repeat`}
        backgroundSize="cover"
        mr={[10, 15, 20, 25]}
        width="23%"
        maxHeight={[180, 180, 180, 195]}
      />
      <Flex width="77%" flexDirection="column">
        <Flex width="100%" mb={[10, 15, 20]} alignItems="center" justifyContent="space-between">
          <Flex mr={10} flexDirection="column">
            <Flex mb={10} alignItems="center">
              <Typography mr={['8px', '8px', 10, 12]} variant="diatype-500" fontSize={[12, 14, 16, 20]}>
                {`${roomNumber} · ${getFloorLabel(floor)}`}
              </Typography>
              <PricingDetails
                locationTimeFormat={currentLocation.timeFormat}
                baseCreditPrice={creditPrices?.base?.creditsPerHour}
                peakTimeCreditPrice={creditPrices?.peakPrice?.creditsPerHour}
                peakTimeDays={creditPrices?.peakPrice?.timeFrames.daysOfWeek}
                peakTimeIntervals={creditPrices?.peakPrice?.timeFrames.times}
                isClickable={!user.isActivate && creditPrices?.peakPrice}
                {...(isMinBookingDetailsShown && { minBookingHours: ruleMinBookingHours })}>
                {!user.isActivate && creditPrices?.peakPrice && (
                  <Typography mr="5px" variant="diatype-400" fontSize={[10, 10, 12, 14]} color={theme.color.gray[300]}>
                    from
                  </Typography>
                )}
                <Icon mr="5px" SVG={Credits} width={[10, 13, 16]} height={[10, 13, 16]} />
                <Typography
                  whiteSpace="nowrap"
                  variant="diatype-400"
                  fontSize={[10, 10, 12, 14]}
                  color={theme.color.gray[300]}>
                  {`${creditsPrice} / hr${
                    user.isActivate ? ` or ${getCurrency(currentLocation.currency, currencyPrice)} / hr` : ''
                  }`}
                </Typography>
              </PricingDetails>
            </Flex>
            <Flex flexWrap="wrap" alignItems="center">
              <Icon mb={10} SVG={Seats} width={[10, 14, 18]} height={[8, 10, 12]} mr={['6px', '8px', 10]} />
              <Typography
                {...(!!features.length && { borderRight: `1px solid ${theme.color.black}` })}
                pr={['6px', '6px', '8px', 10]}
                variant="diatype-400"
                fontSize={[10, 12, 14, 16]}
                mb={10}
                color={theme.color.gray[300]}
                mr={['6px', '8px', 10]}>
                {`${seats} ${seats === 1 ? 'seat' : 'seats'}`}
              </Typography>
              {featuresBlock}
            </Flex>
          </Flex>
          <Button
            alignSelf="flex-start"
            py={['6px', '8px', 10, 12]}
            px={[20, 25, 30, 35]}
            variant="primary"
            fontSize={[10, 10, 13, 16]}
            onClick={onRoomClick(id, startTime, endTime)}
            disabled={!isSelectedTimelineAvailable || (endTime === -1 && startTime === -1)}>
            Book this room
          </Button>
        </Flex>
        <Flex width="100%" position="relative" flexDirection="column">
          <Timeline
            userRole={user?.role}
            bookings={bookings}
            timeZone={currentLocation.timeZone}
            timeFormat={currentLocation.timeFormat}
            timeList={timeList}
            scrollTo={timelineScrollTo}
            onTimeClick={handleTimeClick}
            startTime={startTime}
            endTime={endTime}
            availableTimes={availableTimes}
            onTimeChange={handleTimeChange}
          />
          {isLoading && <Loader position="absolute" background={theme.color.white} width={40} height={40} />}
        </Flex>
      </Flex>
    </Flex>
  );
};

RoomCard.defaultProps = {
  initialStartTime: -1,
  initialEndTime: -1,
  creditPrices: {},
  maxBookingHours: 1,
  minBookingHours: 3,
  ruleMinBookingHours: null,
  defaultBookingHours: 1,
  bookings: [],
  id: 0,
  imageUrl: '',
  number: '',
  floor: 1,
  seats: 1,
  creditsPrice: 0,
  currencyPrice: 0,
  timeList: [],
  availableTimes: [],
  features: [],
};

RoomCard.propTypes = {
  roomType: string.isRequired,
  initialStartTime: number,
  initialEndTime: number,
  creditPrices: exact({
    base: exact({
      creditsPerHour: number,
    }),
    peakPrice: exact({
      creditsPerHour: number,
      timeFrames: exact({
        daysOfWeek: number,
        times: arrayOf(
          exact({
            from: string,
            to: string,
          }),
        ),
      }),
    }),
  }),
  maxBookingHours: number,
  minBookingHours: number,
  ruleMinBookingHours: number,
  defaultBookingHours: number,
  bookings: arrayOf(
    exact({
      fromDateTime: string,
      toDateTime: string,
      company: string,
    }),
  ),
  timelineScrollTo: number.isRequired,
  id: number,
  onRoomClick: func.isRequired,
  imageUrl: string,
  number: string,
  floor: number,
  seats: number,
  creditsPrice: number,
  currencyPrice: number,
  timeList: arrayOf(shape({})),
  availableTimes: arrayOf(shape({})),
  features: arrayOf(
    exact({
      id: number,
      displayName: string,
      iconUrl: string,
      key: string,
    }),
  ),
};

export default RoomCard;
